/**
 * F-024 tenant switcher — dropdown in the topbar for users with N>1 tenants.
 *
 * Role-aware visibility: hidden entirely for single-tenant users (returns null).
 *
 * Styling matches the rest of the topbar — design-system.css CSS variables
 * (`var(--ink)`, `var(--bg)`, `var(--surface)`, `var(--hairline)`) rather
 * than Tailwind utility classes, because that's what `.topbar`, `.icon-btn`,
 * and `.nav-item` use throughout the app. Inventing new Tailwind utility
 * names like `bg-bg-canvas` would not resolve.
 *
 * Active tenant is shown in the trigger label; the dropdown lists all
 * tenants with the active one highlighted (semibold + brand teal). Click
 * outside or Escape closes the menu.
 *
 * ELR-08 (2026-09-21): a switch can now fail without throwing. The store
 * refuses to move `activeTenantId` when the backend stamp or the session
 * refresh fails, and records why in `switchError`. Before this, the menu simply
 * closed and the old workspace stayed selected with nothing said, which reads
 * as a click that did not register. The message below the control is that
 * explanation. It sits OUTSIDE the `open &&` menu on purpose: picking a tenant
 * closes the menu before the async switch settles, so a message inside the list
 * would never be seen. The store clears `switchError` at the start of every
 * attempt, so a successful retry removes it.
 *
 * The provider's own text (`refresh_token_not_found` and friends) is not shown.
 * It is diagnostic, it is not copy, and the user's next action is the same
 * whatever the cause.
 */

import { useState, useRef, useEffect } from 'react';
import { useTenantStore } from '../../lib/tenant-store';

export function TenantSwitcher() {
  const { activeTenantId, tenants, switchTenant, switchError } = useTenantStore();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Role-aware visibility — single-tenant users never see the switcher.
  if (tenants.length <= 1) return null;

  const active = tenants.find((t) => t.id === activeTenantId);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        aria-label="Switch tenant"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          // Cohesion pass phase 3: the switcher sits in the topbar of BOTH
          // shells beside 34px icon buttons and a `.btn-secondary`, and it was
          // 40px tall with a 12px radius, so the one control that crosses
          // between the two halves of the product was the one that matched
          // neither. `--icon-btn` (34) and `--r-control` (10) are the tokens
          // phase 1 settled.
          height: 'var(--icon-btn)',
          padding: '0 14px',
          borderRadius: 'var(--r-control)',
          border: '1px solid var(--hairline)',
          background: 'transparent',
          color: 'var(--ink-2)',
          fontFamily: 'var(--sans)',
          fontSize: 13.5,
          cursor: 'pointer',
          transition: 'background 160ms var(--ease-cushion)',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <span style={{ color: 'var(--ink)', fontWeight: 500 }}>
          {active?.name ?? 'Select tenant'}
        </span>
        <svg
          width={14}
          height={14}
          viewBox="0 0 20 20"
          aria-hidden="true"
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 160ms var(--ease-cushion)',
          }}
        >
          <path
            d="M5 8l5 5 5-5"
            stroke="currentColor"
            fill="none"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 260,
            padding: 6,
            background: 'var(--bg)',
            border: '1px solid var(--hairline-2)',
            borderRadius: 14,
            boxShadow:
              '0 12px 32px rgba(20, 18, 12, 0.08), 0 2px 4px rgba(20, 18, 12, 0.04)',
            zIndex: 50,
          }}
        >
          {tenants.map((t) => {
            const isActive = t.id === activeTenantId;
            return (
              <button
                key={t.id}
                role="menuitem"
                type="button"
                onClick={async () => {
                  setOpen(false);
                  if (!isActive) await switchTenant(t.id);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: 9,
                  border: 'none',
                  background: 'transparent',
                  color: isActive ? 'var(--primary-700)' : 'var(--ink)',
                  fontFamily: 'var(--sans)',
                  fontSize: 13.5,
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'background 120ms var(--ease-cushion)',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = 'var(--surface)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}

      {switchError !== null && (
        <div
          role="alert"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 260,
            padding: '8px 12px',
            background: 'var(--bg)',
            border: '1px solid var(--hairline-2)',
            borderRadius: 10,
            color: 'var(--ink-2)',
            fontFamily: 'var(--sans)',
            fontSize: 12.5,
            lineHeight: 1.45,
            textAlign: 'left',
            zIndex: 49,
          }}
        >
          Could not switch workspace. Please try again.
        </div>
      )}
    </div>
  );
}
